import { ACTIVATE_PRECONDITIONS } from '../constants/Entitlements';
import { RESELLERS } from '../constants/ResellersAndPartners';
import { TierPresentationsOneLine } from '../constants/TierPresentations';
import {
	RESELLER_ACTIVATION__VOUCHER_TITLE,
	RESELLER_ACTIVATION_TITLE,
	RESELLER_ACTIVATION_DESCRIPTION,
	RESELLER_FINALIZE_DESCRIPTION,
	RESELLER_ACTIVATION_DESCRIPTION_FREE,
	RESELLER_FINALIZE_DESCRIPTION_FREE,
	RESELLER_ACTIVATION_DESCRIPTION_TERMED,
	RESELLER_ACTIVATION_DESCRIPTION_TERMED_HAS_SUBSCRIPTION,
	RESELLER_FINALIZE_DESCRIPTION_TERMED,
	RESELLER_FINALIZE_DESCRIPTION_TERMED_HAS_SUBSCRIPTION,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_NON_CANCELLABLE,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_NON_CANCELLABLE,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_TERMED,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_TERMED,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_HAS_SUBSCRIPTION,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_HAS_SUBSCRIPTION,
	RESELLER_ACTIVATION_DESCRIPTION_PROMO_ONE_CENT,
	RESELLER_ACTIVATION_DESCRIPTION_PROMO_WITH_PRICE,
	RESELLER_FINALIZE_DESCRIPTION_PROMO,
	RESELLER_ACTIVATION_DESCRIPTION_PROMO_HAS_SUBSCRIPTION,
	RESELLER_FINALIZE_DESCRIPTION_PROMO_HAS_SUBSCRIPTION,
	RESELLER_ACTIVATION_DESCRIPTION_PROMO_TERMED,
	RESELLER_FINALIZE_DESCRIPTION_PROMO_TERMED,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_HAS_SUBSCRIPTION_ONETIME,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_HAS_SUBSCRIPTION_ONETIME,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_ONETIME,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_ONETIME_NON_CANCELLABLE,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_ONETIME,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_ONETIME_NON_CANCELLABLE,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_TERMED_ONETIME,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_TERMED_ONETIME,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_AND_PROMO,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_AND_PROMO,
	RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_AND_PROMO_NON_CANCELLABLE,
	RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_AND_PROMO_NON_CANCELLABLE,
	RESELLER_ACTIVATION_DESCRIPTION_TERMED_12M_DISCOUNT,
	RESELLER_ACTIVATION_DESCRIPTION_SUMMER_OFFER,
} from '../constants/ResellerCopy';
import { friendlyDate, friendlyDuration } from './datetime';
import { CANCELLATION_EFFECTIVE } from '../constants/Plans';
import { formatPrice } from './number';

function extendCopyWithSubscriptionInfo(copy, hasSubscription) {
	if (!copy) return;

	if (!hasSubscription) {
		return copy.default;
	}

	const subscriberCopy = copy?.tieringHasSubscription || copy?.hasSubscription;
	return { ...copy.default, ...subscriberCopy };
}

function extendCopyWithPrecondition(copy, precondition) {
	if (precondition) {
		return { ...copy, description: [...copy.description, precondition] };
	}
	return copy;
}

export function getActivationCopy({
	plan,
	hasSubscription,
	isLoggedIn,
	activatePrecondition,
	redemptionLeadsToSuspensionOfCurrentSubscription = false,
} = {}) {
	const activateCopy = plan?.copy?.activation;

	if (!activateCopy) return undefined;
	const precondition = getActivatePreconditionCopy({
		activatePrecondition,
		redemptionLeadsToSuspensionOfCurrentSubscription,
		activateCopy,
	});
	const copyWithSubscriptionInfo = extendCopyWithSubscriptionInfo(activateCopy, hasSubscription);

	if (!isLoggedIn) {
		const loggedOutTitle = activateCopy?.loggedOut?.title;
		if (loggedOutTitle) {
			copyWithSubscriptionInfo.title = loggedOutTitle;
		}
	}

	return extendCopyWithPrecondition(copyWithSubscriptionInfo, precondition);
}

export function getFinalizeCopy({
	plan,
	activatePrecondition,
	hasSuspendedSubscription = false,
} = {}) {
	const finalizeCopy = plan?.copy?.finalize;
	if (!finalizeCopy) return undefined;
	const precondition = getFinalizePreconditionCopy({
		activatePrecondition,
		hasSuspendedSubscription,
		finalizeCopy,
	});
	const copyWithSubscriptionInfo = extendCopyWithSubscriptionInfo(finalizeCopy, true);
	return extendCopyWithPrecondition(copyWithSubscriptionInfo, precondition);
}

export function getFinalizePreconditionCopy({
	activatePrecondition,
	hasSuspendedSubscription,
	finalizeCopy,
}) {
	if (
		activatePrecondition === ACTIVATE_PRECONDITIONS.SUBSCRIPTION_WILL_BE_PAUSED ||
		hasSuspendedSubscription
	) {
		return finalizeCopy.precondition?.subscriptionIsPaused;
	} else if (activatePrecondition === ACTIVATE_PRECONDITIONS.SUBSCRIPTION_WILL_BE_CANCELLED) {
		return finalizeCopy.precondition?.subscriptionIsCancelled;
	}
}

export function getActivatePreconditionCopy({
	activatePrecondition,
	redemptionLeadsToSuspensionOfCurrentSubscription,
	activateCopy,
}) {
	if (
		activatePrecondition === ACTIVATE_PRECONDITIONS.SUBSCRIPTION_WILL_BE_PAUSED ||
		redemptionLeadsToSuspensionOfCurrentSubscription
	) {
		return activateCopy.precondition?.subscriptionWillPause;
	} else if (activatePrecondition === ACTIVATE_PRECONDITIONS.SUBSCRIPTION_WILL_BE_CANCELLED) {
		return activateCopy.precondition?.subscriptionWillCancel;
	}
}

export function getResellerDescription(
	reseller,
	planDescription,
	hasPaidSubscription,
	isFinalizeCopy = false,
	voucherPlanId = null
) {
	const resellerName = RESELLERS.find((el) => el.id === reseller)?.displayName || reseller;
	const isNonCancellable =
		planDescription.discountedPeriod?.cancellationEffective ===
		CANCELLATION_EFFECTIVE.NOT_IN_THIS_PHASE;
	let resellerCopy;
	if (planDescription.discountedPeriod && planDescription.promoPeriod) {
		resellerCopy = (
			isFinalizeCopy
				? isNonCancellable
					? RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_AND_PROMO_NON_CANCELLABLE
					: RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_AND_PROMO
				: isNonCancellable
				? RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_AND_PROMO_NON_CANCELLABLE
				: RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_AND_PROMO
		).map((line) =>
			line
				.replace('{{campaign}}', resellerName)
				.replace('{{discount_price}}', formatPrice(planDescription.discountedPeriod.price))
				.replace('{{discount_date}}', friendlyDate(planDescription.discountedPeriod.startDate))
				.replace('{{discount_period}}', friendlyDuration(planDescription.discountedPeriod.duration))
				.replace('{{promo_price}}', formatPrice(planDescription.promoPeriod.price))
				.replace('{{promo_period}}', friendlyDuration(planDescription.promoPeriod.duration))
				.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
				.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
		);
	} else if (planDescription.discountedPeriod) {
		if (hasPaidSubscription) {
			if (planDescription.discountedPeriod.isOneTimePayment) {
				resellerCopy = (
					isFinalizeCopy
						? RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_HAS_SUBSCRIPTION_ONETIME
						: RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_HAS_SUBSCRIPTION_ONETIME
				).map((line) =>
					line
						.replace('{{campaign}}', resellerName)
						.replace('{{discount_price}}', formatPrice(planDescription.discountedPeriod.price))
						.replace('{{period}}', friendlyDuration(planDescription.discountedPeriod.duration))
						.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
						.replace('{{date}}', friendlyDate(planDescription.normalPeriod.startDate))
						.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
				);
			} else {
				resellerCopy = (
					isFinalizeCopy
						? RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_HAS_SUBSCRIPTION
						: RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_HAS_SUBSCRIPTION
				).map((line) =>
					line
						.replace('{{campaign}}', resellerName)
						.replace('{{discount_price}}', formatPrice(planDescription.discountedPeriod.price))
						.replace('{{period}}', friendlyDuration(planDescription.discountedPeriod.duration))
						.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
						.replace('{{date}}', friendlyDate(planDescription.normalPeriod.startDate))
						.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
				);
			}
		} else {
			if (planDescription.normalPeriod.duration === null) {
				if (planDescription.discountedPeriod.isOneTimePayment) {
					resellerCopy = (
						isFinalizeCopy
							? isNonCancellable
								? RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_ONETIME_NON_CANCELLABLE
								: RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_ONETIME
							: isNonCancellable
							? RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_ONETIME_NON_CANCELLABLE
							: RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_ONETIME
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{discount_price}}', formatPrice(planDescription.discountedPeriod.price))
							.replace('{{period}}', friendlyDuration(planDescription.discountedPeriod.duration))
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
							.replace('{{date}}', friendlyDate(planDescription.normalPeriod.startDate))
							.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
					);
				} else {
					resellerCopy = (
						isFinalizeCopy
							? isNonCancellable
								? RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_NON_CANCELLABLE
								: RESELLER_FINALIZE_DESCRIPTION_DISCOUNT
							: isNonCancellable
							? RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_NON_CANCELLABLE
							: RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{discount_price}}', formatPrice(planDescription.discountedPeriod.price))
							.replace('{{period}}', friendlyDuration(planDescription.discountedPeriod.duration))
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
							.replace('{{date}}', friendlyDate(planDescription.normalPeriod.startDate))
							.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
					);
				}
			} else {
				if (planDescription.discountedPeriod.isOneTimePayment) {
					resellerCopy = (
						isFinalizeCopy
							? RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_TERMED_ONETIME
							: RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_TERMED_ONETIME
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{discount_price}}', formatPrice(planDescription.discountedPeriod.price))
							.replace('{{period}}', friendlyDuration(planDescription.discountedPeriod.duration))
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
					);
				} else {
					resellerCopy = (
						isFinalizeCopy
							? RESELLER_FINALIZE_DESCRIPTION_DISCOUNT_TERMED
							: RESELLER_ACTIVATION_DESCRIPTION_DISCOUNT_TERMED
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{discount_price}}', formatPrice(planDescription.discountedPeriod.price))
							.replace('{{period}}', friendlyDuration(planDescription.discountedPeriod.duration))
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
					);
				}
			}
		}
	} else if (planDescription.promoPeriod) {
		if (hasPaidSubscription) {
			resellerCopy = (
				isFinalizeCopy
					? RESELLER_FINALIZE_DESCRIPTION_PROMO_HAS_SUBSCRIPTION
					: RESELLER_ACTIVATION_DESCRIPTION_PROMO_HAS_SUBSCRIPTION
			).map((line) =>
				line
					.replace('{{campaign}}', resellerName)
					.replace('{{period}}', friendlyDuration(planDescription.promoPeriod.duration))
					.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
					.replace('{{date}}', friendlyDate(planDescription.normalPeriod.startDate))
			);
		} else {
			if (planDescription.normalPeriod.duration === null) {
				if (planDescription.promoPeriod.price > 0.01) {
					const activationCopy =
						voucherPlanId === 'PlusLongTermContract3M'
							? RESELLER_ACTIVATION_DESCRIPTION_SUMMER_OFFER
							: RESELLER_ACTIVATION_DESCRIPTION_PROMO_WITH_PRICE;
					resellerCopy = (
						isFinalizeCopy ? RESELLER_FINALIZE_DESCRIPTION_PROMO : activationCopy
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{period}}', friendlyDuration(planDescription.promoPeriod.duration))
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
							.replace('{{date}}', friendlyDate(planDescription.normalPeriod.startDate))
							.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
							.replace('{{promo_price}}', formatPrice(planDescription.promoPeriod.price))
					);
				} else if (planDescription.promoPeriod.price === 0.01) {
					resellerCopy = (
						isFinalizeCopy
							? RESELLER_FINALIZE_DESCRIPTION_PROMO
							: RESELLER_ACTIVATION_DESCRIPTION_PROMO_ONE_CENT
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{period}}', friendlyDuration(planDescription.promoPeriod.duration))
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
							.replace('{{date}}', friendlyDate(planDescription.normalPeriod.startDate))
							.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
					);
				}
			} else {
				resellerCopy = (
					isFinalizeCopy
						? RESELLER_FINALIZE_DESCRIPTION_PROMO_TERMED
						: RESELLER_ACTIVATION_DESCRIPTION_PROMO_TERMED
				).map((line) =>
					line
						.replace('{{campaign}}', resellerName)
						.replace('{{period}}', friendlyDuration(planDescription.promoPeriod.duration))
						.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
				);
			}
		}
	} else {
		if (planDescription.normalPeriod.duration === null) {
			if (planDescription.normalPeriod.price === 0) {
				resellerCopy = (
					isFinalizeCopy ? RESELLER_FINALIZE_DESCRIPTION_FREE : RESELLER_ACTIVATION_DESCRIPTION_FREE
				).map((line) =>
					line
						.replace('{{campaign}}', resellerName)
						.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
				);
			} else {
				resellerCopy = (
					isFinalizeCopy ? RESELLER_FINALIZE_DESCRIPTION : RESELLER_ACTIVATION_DESCRIPTION
				).map((line) =>
					line
						.replace('{{campaign}}', resellerName)
						.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
						.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
				);
			}
		} else {
			if (hasPaidSubscription) {
				if (isPriceIncreasePlan(voucherPlanId)) {
					resellerCopy = (
						isFinalizeCopy
							? RESELLER_FINALIZE_DESCRIPTION_TERMED_HAS_SUBSCRIPTION
							: RESELLER_ACTIVATION_DESCRIPTION_TERMED_12M_DISCOUNT
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
							.replace('{{period}}', friendlyDuration(planDescription.normalPeriod.duration))
							.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
					);
				} else {
					resellerCopy = (
						isFinalizeCopy
							? RESELLER_FINALIZE_DESCRIPTION_TERMED_HAS_SUBSCRIPTION
							: RESELLER_ACTIVATION_DESCRIPTION_TERMED_HAS_SUBSCRIPTION
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
							.replace('{{period}}', friendlyDuration(planDescription.normalPeriod.duration))
					);
				}
			} else {
				if (isPriceIncreasePlan(voucherPlanId)) {
					resellerCopy = (
						isFinalizeCopy
							? RESELLER_FINALIZE_DESCRIPTION_TERMED
							: RESELLER_ACTIVATION_DESCRIPTION_TERMED_12M_DISCOUNT
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
							.replace('{{period}}', friendlyDuration(planDescription.normalPeriod.duration))
							.replace('{{price}}', formatPrice(planDescription.normalPeriod.price))
					);
				} else {
					resellerCopy = (
						isFinalizeCopy
							? RESELLER_FINALIZE_DESCRIPTION_TERMED
							: RESELLER_ACTIVATION_DESCRIPTION_TERMED
					).map((line) =>
						line
							.replace('{{campaign}}', resellerName)
							.replace('{{tier}}', TierPresentationsOneLine[planDescription.tierId])
							.replace('{{period}}', friendlyDuration(planDescription.normalPeriod.duration))
					);
				}
			}
		}
	}

	return resellerCopy;
}

export function getResellerTitle(voucherPlanId) {
	if (isPriceIncreasePlan(voucherPlanId)) {
		return RESELLER_ACTIVATION__VOUCHER_TITLE;
	} else {
		return RESELLER_ACTIVATION_TITLE;
	}
}

export const isPriceIncreasePlan = (planId) =>
	planId === 'PremiumTermed12M1EurDiscount' ||
	planId === 'PlusTermed12M1EurDiscount' ||
	planId === 'EssentialTermed12M1EurDiscount' ||
	planId === 'PremiumTermed12M2EurDiscount';
